import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../apiConfig";

const isEnabled = (value) => {
  return Number(value) === 1 || value === true || value === "true";
};

function PrintSale() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const hasLoaded = useRef(false);
  const hasPrinted = useRef(false);

  const authHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
    },
  });

  useEffect(() => {
    if (hasLoaded.current) return;

    hasLoaded.current = true;
    loadPrintData();
  }, [id]);

  useEffect(() => {
    const handleAfterPrint = () => {
      redirectToSoftware();
    };

    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const redirectToSoftware = () => {
    try {
      window.close();

      setTimeout(() => {
        if (!window.closed) {
          navigate("/sales", { replace: true });
        }
      }, 300);
    } catch {
      navigate("/sales", { replace: true });
    }
  };

  const loadPrintData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/sales/${id}/print`, authHeader());

      if (!res.data?.success || !res.data?.data) {
        setLoading(false);
        alert("No bill data found");
        navigate("/sales", { replace: true });
        return;
      }

      setData(res.data.data);
      setLoading(false);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!hasPrinted.current) {
            hasPrinted.current = true;
            window.print();
          }
        });
      });
    } catch (error) {
      console.error("Print loading error:", error);
      setLoading(false);
      alert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Print data loading failed"
      );
      navigate("/sales", { replace: true });
    }
  };

  const formatMoney = (value) => {
    return Number(value || 0).toFixed(2);
  };

  const formatDate = (date) => {
    if (!date) return "";

    const cleanDate = String(date).slice(0, 10);
    const parts = cleanDate.split("-");

    if (parts.length !== 3) return cleanDate;

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const paperClass = useMemo(() => {
    const paperSize = String(data?.settings?.paper_size || "A5").toUpperCase();
    const thermalWidth = String(data?.settings?.thermal_width || "80");

    if (paperSize === "A4") return "print-a4";
    if (paperSize === "A5") return "print-a5";

    if (paperSize === "THERMAL") {
      return thermalWidth === "58"
        ? "print-thermal-58"
        : "print-thermal-80";
    }

    return "print-a5";
  }, [data]);

  if (loading) {
    return <div className="print-loading">Loading bill...</div>;
  }

  if (!data) {
    return <div className="print-loading">No bill data found</div>;
  }

  const {
    sale = {},
    items = [],
    customer = null,
    firm = {},
    settings = {},
  } = data;

  const isThermal =
    String(settings?.paper_size || "").toUpperCase() === "THERMAL";

  if (isThermal) {
    return (
      <div className={`print-page ${paperClass}`}>
        <ThermalBill
          sale={sale}
          items={items}
          customer={customer}
          firm={firm}
          settings={settings}
          formatMoney={formatMoney}
          formatDate={formatDate}
        />
      </div>
    );
  }

  return (
    <div className={`print-page ${paperClass}`}>
      <StandardBill
        sale={sale}
        items={items}
        customer={customer}
        firm={firm}
        settings={settings}
        formatMoney={formatMoney}
        formatDate={formatDate}
      />
    </div>
  );
}

function StandardBill({
  sale,
  items,
  customer,
  firm,
  settings,
  formatMoney,
  formatDate,
}) {
  const showLogo = isEnabled(settings?.show_logo);
  const showGst = isEnabled(settings?.show_gst);
  const showBankDetails = isEnabled(settings?.show_bank_details);

  const total = Number(sale.gross_amount || 0);
  const gstTotal = Number(sale.gst_amount || 0);
  const cgst = gstTotal / 2;
  const sgst = gstTotal / 2;
  const totalAfterGst = total + gstTotal;
  const discount = Number(sale.discount_amount || 0);
  const payable = Number(sale.net_amount || 0);

  const cashAmount = Number(sale.cash_amount || 0);
  const upiAmount = Number(sale.upi_amount || 0);
  const cardAmount = Number(sale.card_amount || 0);
  const pendingAmount = Number(
    sale.pending_amount || sale.credit_amount || 0
  );

  const bankName =
    firm?.bank_name ||
    firm?.bank ||
    settings?.bank_name ||
    "";

  const accountName =
    firm?.account_name ||
    firm?.account_holder_name ||
    settings?.account_name ||
    "";

  const accountNo =
    firm?.account_no ||
    firm?.account_number ||
    firm?.bank_account_no ||
    settings?.account_no ||
    "";

  const ifscCode =
    firm?.ifsc_code ||
    firm?.ifsc ||
    settings?.ifsc_code ||
    "";

  const upiId =
    firm?.upi_id ||
    settings?.upi_id ||
    "";

  const hasBankData =
    bankName || accountName || accountNo || ifscCode || upiId;

  return (
    <div className="invoice-box">
      <div className="invoice-header">
        <div className="firm-left">
          {showLogo && firm?.logo_base64 && (
            <img
              src={firm.logo_base64}
              alt="Firm Logo"
              className="firm-logo"
            />
          )}

          <div>
            <h1>{firm?.firm_name || "Firm Name"}</h1>

            {firm?.address && <p>{firm.address}</p>}

            <div className="firm-line">
              {firm?.mobile && <span>Mob: {firm.mobile}</span>}
              {firm?.email && <span>Email: {firm.email}</span>}
            </div>

            {showGst && firm?.gst_no && (
              <p>GST No: {firm.gst_no}</p>
            )}
          </div>
        </div>

        <div className="invoice-title">
          <h2>SALE INVOICE</h2>
          <p>{sale.bill_type || "NON_GST"}</p>
        </div>
      </div>

      <div className="invoice-info-grid">
        <div>
          <h4>Bill To</h4>

          <p>
            <b>{customer?.customer_name || "Walk-in Customer"}</b>
          </p>

          {customer?.mobile_no && (
            <p>Mobile: {customer.mobile_no}</p>
          )}

          {customer?.address && (
            <p>Address: {customer.address}</p>
          )}

          {showGst && customer?.gst_no && (
            <p>GST No: {customer.gst_no}</p>
          )}
        </div>

        <div>
          <p>
            <b>Bill No:</b> {sale.bill_no || "-"}
          </p>

          <p>
            <b>Date:</b> {formatDate(sale.bill_date)}
          </p>

          <p>
            <b>Bill Type:</b> {sale.bill_type || "NON_GST"}
          </p>
        </div>
      </div>

      <table
        className={`invoice-table ${
          showGst ? "invoice-table-gst" : "invoice-table-non-gst"
        }`}
      >
        <thead>
          <tr>
            <th>Sr No</th>
            <th>Product</th>
            <th className="right">Rate</th>
            <th className="right">Qty</th>
            <th className="right">Total</th>

            {showGst && (
              <>
                <th className="right">GST</th>
                <th className="right">Total After GST</th>
              </>
            )}
          </tr>
        </thead>

        <tbody>
          {items.length === 0 ? (
            <tr>
              <td
                colSpan={showGst ? 7 : 5}
                style={{ textAlign: "center" }}
              >
                No items found
              </td>
            </tr>
          ) : (
            items.map((row, index) => {
              const qty = Number(row.qty || 0);
              const rate = Number(row.rate || 0);
              const rowTotal = Number(
                row.taxable_amount || qty * rate || 0
              );
              const rowGst = Number(row.gst_amount || 0);
              const rowAfterGst = Number(
                row.amount || rowTotal + rowGst || 0
              );

              return (
                <tr key={row.id || row.item_key || index}>
                  <td>{index + 1}</td>
                  <td>{row.product_name || "-"}</td>
                  <td className="right">{formatMoney(rate)}</td>
                  <td className="right">{formatMoney(qty)}</td>
                  <td className="right">{formatMoney(rowTotal)}</td>

                  {showGst && (
                    <>
                      <td className="right">
                        {formatMoney(rowGst)}
                      </td>

                      <td className="right">
                        {formatMoney(rowAfterGst)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <div className="invoice-bottom">
        <div className="invoice-note">
          <div className="amount-words-title">
            Amount in Words
          </div>

          <div className="amount-words-text">
            Rupees {numberToWordsIndian(Math.round(payable))} Only
          </div>

          <div className="collection-mode-box">
            <div className="collection-title">
              Collection Mode
            </div>

            <div className="collection-modern">
              {cashAmount > 0 && (
                <div className="payment-chip cash">
                  <span>Cash</span>
                  <b>₹{formatMoney(cashAmount)}</b>
                </div>
              )}

              {upiAmount > 0 && (
                <div className="payment-chip upi">
                  <span>UPI</span>
                  <b>₹{formatMoney(upiAmount)}</b>
                </div>
              )}

              {cardAmount > 0 && (
                <div className="payment-chip card">
                  <span>Card</span>
                  <b>₹{formatMoney(cardAmount)}</b>
                </div>
              )}

              {pendingAmount > 0 && (
                <div className="payment-chip pending">
                  <span>Credit</span>
                  <b>₹{formatMoney(pendingAmount)}</b>
                </div>
              )}

              {cashAmount <= 0 &&
                upiAmount <= 0 &&
                cardAmount <= 0 &&
                pendingAmount <= 0 && (
                  <div className="payment-chip">
                    <span>No payment entry</span>
                  </div>
                )}
            </div>
          </div>

          {showBankDetails && hasBankData && (
            <div className="bank-details">
              <h4>Bank Details</h4>

              {accountName && (
                <p>
                  <b>Account Name:</b> {accountName}
                </p>
              )}

              {bankName && (
                <p>
                  <b>Bank:</b> {bankName}
                </p>
              )}

              {accountNo && (
                <p>
                  <b>Account No:</b> {accountNo}
                </p>
              )}

              {ifscCode && (
                <p>
                  <b>IFSC:</b> {ifscCode}
                </p>
              )}

              {upiId && (
                <p>
                  <b>UPI ID:</b> {upiId}
                </p>
              )}
            </div>
          )}

          {firm?.terms_conditions && (
            <div className="terms">
              <h4>Terms & Conditions</h4>
              <p>{firm.terms_conditions}</p>
            </div>
          )}
        </div>

        <div className="invoice-summary">
          <SummaryRow label="Subtotal" value={total} />

          {showGst && (
            <>
              <SummaryRow label="CGST" value={cgst} />
              <SummaryRow label="SGST" value={sgst} />
              <SummaryRow
                label="Total After GST"
                value={totalAfterGst}
              />
            </>
          )}

          <SummaryRow label="Discount" value={discount} />

          <SummaryRow
            label="Grand Total"
            value={payable}
            strong
          />
        </div>
      </div>

      {settings?.footer_text && (
        <div className="invoice-footer">
          {settings.footer_text}
        </div>
      )}
    </div>
  );
}

function ThermalBill({
  sale,
  items,
  customer,
  firm,
  settings,
  formatMoney,
  formatDate,
}) {
  const showLogo = isEnabled(settings?.show_logo);
  const showGst = isEnabled(settings?.show_gst);
  const showBankDetails = isEnabled(settings?.show_bank_details);

  const bankName =
    firm?.bank_name ||
    firm?.bank ||
    settings?.bank_name ||
    "";

  const accountName =
    firm?.account_name ||
    firm?.account_holder_name ||
    settings?.account_name ||
    "";

  const accountNo =
    firm?.account_no ||
    firm?.account_number ||
    firm?.bank_account_no ||
    settings?.account_no ||
    "";

  const ifscCode =
    firm?.ifsc_code ||
    firm?.ifsc ||
    settings?.ifsc_code ||
    "";

  const upiId =
    firm?.upi_id ||
    settings?.upi_id ||
    "";

  const hasBankData =
    bankName || accountName || accountNo || ifscCode || upiId;

  return (
    <div className="thermal-bill">
      <div className="thermal-center">
        {showLogo && firm?.logo_base64 && (
          <img
            src={firm.logo_base64}
            alt="Firm Logo"
            className="thermal-logo"
          />
        )}

        <h2>{firm?.firm_name || "Firm Name"}</h2>

        {firm?.address && <p>{firm.address}</p>}

        {firm?.mobile && <p>Mob: {firm.mobile}</p>}

        {showGst && firm?.gst_no && (
          <p>GST: {firm.gst_no}</p>
        )}
      </div>

      <div className="thermal-line" />

      <div className="thermal-info">
        <p>Bill: {sale.bill_no || "-"}</p>
        <p>Date: {formatDate(sale.bill_date)}</p>
        <p>
          Customer: {customer?.customer_name || "Walk-in"}
        </p>
      </div>

      <div className="thermal-line" />

      <table className="thermal-table">
        <thead>
          <tr>
            <th>Item</th>
            <th className="right">Qty</th>
            <th className="right">Amt</th>
          </tr>
        </thead>

        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan="3" style={{ textAlign: "center" }}>
                No items found
              </td>
            </tr>
          ) : (
            items.map((row, index) => (
              <tr key={row.id || row.item_key || index}>
                <td>{row.product_name || "-"}</td>
                <td className="right">
                  {formatMoney(row.qty)}
                </td>
                <td className="right">
                  {formatMoney(
                    showGst
                      ? row.amount
                      : row.taxable_amount ||
                          Number(row.qty || 0) *
                            Number(row.rate || 0)
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="thermal-line" />

      <div className="thermal-summary">
        <SummaryLine
          label="Subtotal"
          value={sale.gross_amount}
        />

        {showGst && (
          <SummaryLine
            label="GST"
            value={sale.gst_amount}
          />
        )}

        <SummaryLine
          label="Discount"
          value={sale.discount_amount}
        />

        <SummaryLine
          label="Grand Total"
          value={sale.net_amount}
          strong
        />

        {Number(sale.cash_amount || 0) > 0 && (
          <SummaryLine
            label="Cash"
            value={sale.cash_amount}
          />
        )}

        {Number(sale.upi_amount || 0) > 0 && (
          <SummaryLine
            label="UPI"
            value={sale.upi_amount}
          />
        )}

        {Number(sale.card_amount || 0) > 0 && (
          <SummaryLine
            label="Card"
            value={sale.card_amount}
          />
        )}

        {Number(
          sale.pending_amount || sale.credit_amount || 0
        ) > 0 && (
          <SummaryLine
            label="Pending"
            value={
              sale.pending_amount || sale.credit_amount
            }
          />
        )}
      </div>

      {showBankDetails && hasBankData && (
        <>
          <div className="thermal-line" />

          <div className="thermal-bank-details">
            <b>Bank Details</b>

            {accountName && <p>A/C Name: {accountName}</p>}
            {bankName && <p>Bank: {bankName}</p>}
            {accountNo && <p>A/C No: {accountNo}</p>}
            {ifscCode && <p>IFSC: {ifscCode}</p>}
            {upiId && <p>UPI: {upiId}</p>}
          </div>
        </>
      )}

      <div className="thermal-line" />

      {settings?.footer_text && (
        <div className="thermal-center">
          <p>{settings.footer_text}</p>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, strong = false }) {
  return (
    <div className={`summary-row ${strong ? "strong" : ""}`}>
      <span>{label}</span>
      <b>₹{Number(value || 0).toFixed(2)}</b>
    </div>
  );
}

function SummaryLine({ label, value, strong = false }) {
  return (
    <div
      className={`thermal-summary-line ${
        strong ? "strong" : ""
      }`}
    >
      <span>{label}</span>
      <b>{Number(value || 0).toFixed(2)}</b>
    </div>
  );
}

function numberToWordsIndian(num) {
  const safeNumber = Math.max(
    0,
    Math.floor(Number(num || 0))
  );

  if (safeNumber === 0) return "Zero";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];

  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertBelowHundred = (number) => {
    if (number < 20) return ones[number];

    return `${tens[Math.floor(number / 10)]} ${
      ones[number % 10]
    }`.trim();
  };

  const convertBelowThousand = (number) => {
    let word = "";
    let remaining = number;

    if (remaining >= 100) {
      word += `${ones[Math.floor(remaining / 100)]} Hundred`;
      remaining %= 100;

      if (remaining > 0) {
        word += " ";
      }
    }

    if (remaining > 0) {
      word += convertBelowHundred(remaining);
    }

    return word;
  };

  let remaining = safeNumber;
  let words = "";

  const crore = Math.floor(remaining / 10000000);
  remaining %= 10000000;

  const lakh = Math.floor(remaining / 100000);
  remaining %= 100000;

  const thousand = Math.floor(remaining / 1000);
  remaining %= 1000;

  if (crore > 0) {
    words += `${convertBelowThousand(crore)} Crore `;
  }

  if (lakh > 0) {
    words += `${convertBelowThousand(lakh)} Lakh `;
  }

  if (thousand > 0) {
    words += `${convertBelowThousand(thousand)} Thousand `;
  }

  if (remaining > 0) {
    words += convertBelowThousand(remaining);
  }

  return words.trim();
}

export default PrintSale;